import { getCountryLabel, getCountryOptions } from '@/utils/countries';

/**
 * Traduction des noms de pays de DESTINATION du parcours Santé/Mobilité.
 *
 * Le référentiel des destinations vient du back (`GET /api/countries`) et porte
 * `id`, `name` et surtout `zone`, qui décide du tarif. On ne peut donc pas le
 * remplacer par `i18n-iso-countries` : on garde l'objet du back tel quel et on
 * ne traduit QUE son affichage.
 *
 * Pourquoi ce pont plutôt qu'une table de traduction côté back : la table
 * `country_translation` existe, mais ne couvre que `fr`, `es` et `en`. Il y
 * manque le chinois et l'arabe, deux des quatre langues du site, et elle porte
 * un espagnol que le site ne propose pas. `i18n-iso-countries`, lui, a les
 * quatre — il est déjà une dépendance du front depuis le composant d'adresse.
 *
 * Le pont se fait par le NOM français, faute de code ISO dans la table `country`
 * du back. C'est une heuristique, assumée comme telle :
 *
 *   - 198 des 232 noms se résolvent seuls, une fois accents et ponctuation
 *     normalisés (les noms du back sont saisis sans accents : « Coree du Sud »)
 *   - 28 demandent un alias explicite, listé ci-dessous
 *   - 6 ne sont pas des pays ISO et ne le seront jamais ; ils gardent leur
 *     libellé d'origine
 *
 * `src/features/sante-mobilite/countryLabel.test.ts` verrouille ces chiffres :
 * si quelqu'un ajoute ou renomme une entrée dans `data/country.csv` côté back
 * sans alias correspondant, le test tombe.
 *
 * Remplacement propre, le jour où on voudra : une colonne `iso_code` sur
 * l'entité `Country`, renseignée une fois avec ce même mapping. L'affichage ne
 * bougerait pas d'un pixel, et cette heuristique disparaîtrait.
 */

/** `Coree du Sud` → `coreedusud`. Sert de clé de rapprochement. */
const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

/**
 * Noms du back que le référentiel ISO ne reconnaît pas seul.
 *
 * Trois familles : des caractères perdus à la saisie (`Curaao` pour Curaçao,
 * `Kowet` pour Koweït), des variantes de nommage (`Birmanie` vs Myanmar,
 * `Swaziland` vs Eswatini), et un nom carrément en anglais (`Comoros`).
 */
const ALIASES: Record<string, string> = {
  azerbadjan: 'AZ',
  birmanie: 'MM',
  bonaire: 'BQ',
  brunei: 'BN',
  centreafrique: 'CF',
  comoros: 'KM',
  congo: 'CG',
  curaao: 'CW',
  etatsfederesdemicronesie: 'FM',
  etatsunis: 'US',
  ilesbermudes: 'BM',
  ilescamans: 'KY',
  ilesfalkland: 'FK',
  ilespalau: 'PW',
  kirghizstan: 'KG',
  kowet: 'KW',
  macedoine: 'MK',
  saintkittsnevis: 'KN',
  saintmartin: 'MF',
  saintvincent: 'VC',
  samoaorientale: 'AS',
  saotome: 'ST',
  surinam: 'SR',
  swaziland: 'SZ',
  tanzanie: 'TZ',
  trinidadettobago: 'TT',
  turksetcaicos: 'TC',
  vatican: 'VA',
};

/**
 * Entrées du référentiel back qui ne sont PAS des pays ISO : régions,
 * territoires, zones conventionnelles. Elles gardent leur libellé français.
 * Listées explicitement pour que le test distingue « pas traduisible, c'est
 * normal » de « pas traduit, quelqu'un a cassé le mapping ».
 */
export const NON_ISO_DESTINATIONS = [
  'Alaska',
  'Canal de Panama',
  'Hawai',
  'Iles des Aores',
  'Madere',
  'Neutre Irak-ArabieSaoud',
];

/** Index nom français normalisé → code ISO2, construit une fois. */
let frenchIndex: Map<string, string> | null = null;

const getFrenchIndex = (): Map<string, string> => {
  // Construit à la première demande : `getCountryOptions` charge et trie les
  // 249 pays, inutile de le faire au chargement du module.
  if (frenchIndex) return frenchIndex;
  frenchIndex = new Map<string, string>();
  for (const { code, label } of getCountryOptions('fr')) {
    frenchIndex.set(normalize(label), code);
  }
  return frenchIndex;
};

/**
 * Code ISO2 correspondant à un nom de destination du back, ou `null` si
 * l'entrée n'est pas un pays ISO.
 */
export const destinationIsoCode = (name?: string | null): string | null => {
  if (!name) return null;
  const key = normalize(name);
  return ALIASES[key] ?? getFrenchIndex().get(key) ?? null;
};

/**
 * Libellé d'une destination dans la langue demandée.
 *
 * Repli sur le nom d'origine quand la destination n'est pas un pays ISO :
 * mieux vaut « Canal de Panama » en français partout qu'une ligne vide.
 */
export const destinationLabel = (
  name: string | null | undefined,
  locale: string,
): string => {
  if (!name) return '';
  const code = destinationIsoCode(name);
  return code ? getCountryLabel(code, locale) : name;
};
